import { Controller } from "@hotwired/stimulus";

// Controls a reusable filter popover that portals to body and positions near a trigger
// Usage:
// <button data-action="click->filter-popover#open">...</button>
// <div data-controller="filter-popover"
//      data-filter-popover-field-value="name"
//      data-action="filter-popover:apply->grid#applyFilter filter-popover:clear->grid#clearFilter"
// >
//   <input data-filter-popover-target="input" data-action="keydown->filter-popover#keydown" />
//   <button data-action="filter-popover#apply">Apply</button>
//   <button data-action="filter-popover#clear">Clear</button>
//   <button data-action="filter-popover#close">Close</button>
// </div>
export default class extends Controller {
  static targets = ["popover", "input"];
  static values = { field: String, portal: { type: Boolean, default: false } };

  initialize() {
    // Cache for the popover element once resolved (so after moving to <body>, we still reference the right node)
    this._popRef = null;
    this._cleanupFns = [];
    this._gridEl = null;
  }

  get popEl() {
    // If we've previously resolved the popover, return the cached element
    if (this._popRef && this._popRef.isConnected) {
      return this._popRef;
    }
    // Prefer explicit target; otherwise find descendant with target attribute
    let el = null;
    if (this.hasPopoverTarget) {
      el = this.popoverTarget;
    } else {
      el = this.element.querySelector('[data-filter-popover-target="popover"]');
    }
    if (el) {
      this._popRef = el;
      return el;
    }
    // Last resort: do NOT return the controller element (to avoid moving the header wrapper)
    // Create a lightweight div as a popover shell if not found (shouldn't happen with correct markup)
    const fallback = document.createElement('div');
    fallback.classList.add('hidden');
    document.body.appendChild(fallback);
    this._popRef = fallback;
    return fallback;
  }

  connect() {
    this._escHandler = (e) => { if (e.key === 'Escape') this.close(); };
    this._outsideHandler = (e) => {
      const pop = this.popEl;
      const insideController = this.element.contains(e.target);
      const insidePopover = pop.contains(e.target);
      const onTrigger = e.target === this._lastTrigger || this._lastTrigger?.contains(e.target);
      if (!insideController && !insidePopover && !onTrigger) {
        this.close();
      }
    };
    // Initialize active icon state from URL (server-side filters)
    this.updateActiveIcon();
  }

  open(e) {
    e.preventDefault();
    e.stopPropagation();
    this._lastTrigger = e.currentTarget;
    // capture the grid element before portaling so we can dispatch events to it later
    this._gridEl = (this.element.closest('[data-controller~="grid"]')) || document.getElementById('table-definitions-grid');
    this.preparePopover();
    this.positionNear(this._lastTrigger);
    document.addEventListener('keydown', this._escHandler);
    document.addEventListener('click', this._outsideHandler, { capture: true });
    this.attachPopoverEvents();
    const input = this.getInputEl();
    if (input) {
      // Prefill from URL query param for this.fieldValue
      const params = new URLSearchParams(window.location.search);
      const filterName = this.fieldValue + '_filter';
      const val = params.get(filterName) || '';
      input.value = val;
      input.focus();
      input.select && val && input.select();
    }
  }

  apply(e) {
    e?.preventDefault();
    const input = this.getInputEl();
    const value = input ? (input.value || '').trim() : '';
    const target = this._gridEl || document.querySelector('[data-controller~="grid"]') || document.body;
    const filterName = this.fieldValue + '_filter';
    target.dispatchEvent(new CustomEvent('filter-popover:apply', { detail: { field: filterName, value }, bubbles: true }));
    this.close();
  }

  clear(e) {
    e.preventDefault();
    const input = this.getInputEl();
    if (input) input.value = '';
    const target = this._gridEl || document.querySelector('[data-controller~="grid"]') || document.body;
    const filterName = this.fieldValue + '_filter';
    target.dispatchEvent(new CustomEvent('filter-popover:clear', { detail: { field: filterName }, bubbles: true }));
  }

  keydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.apply(e);
    }
  }

  close() {
    const pop = this.popEl;
    pop.classList.add('hidden');
    document.removeEventListener('keydown', this._escHandler);
    document.removeEventListener('click', this._outsideHandler, { capture: true });
    this.detachPopoverEvents();
  }

  preparePopover() {
    const pop = this.popEl;
    if (this.portalValue) {
      if (pop.parentElement !== document.body) {
        document.body.appendChild(pop);
      }
      Object.assign(pop.style, { position: 'fixed', zIndex: 9999 });
    } else {
      // Keep in place; rely on z-index and absolute positioning
      if (pop.parentElement !== this.element && pop.dataset.filterPopoverTarget === 'popover') {
        // If previously portaled, move it back under the controller wrapper
        this.element.appendChild(pop);
      }
      Object.assign(pop.style, { position: 'absolute', zIndex: 50 });
    }
    pop.classList.remove('hidden');
    // Re-measure after showing
    pop.getBoundingClientRect();
  }

  positionNear(trigger) {
    const pop = this.popEl;
    const rect = trigger.getBoundingClientRect();
    const popRect = pop.getBoundingClientRect();
    const margin = 6;
    if (this.portalValue) {
      // fixed relative to viewport
      let top = rect.bottom + margin;
      let left = Math.min(rect.right - popRect.width, window.innerWidth - popRect.width - margin);
      left = Math.max(left, margin);
      if (top + popRect.height > window.innerHeight - margin) {
        top = rect.top - popRect.height - margin;
      }
      pop.style.top = `${top}px`;
      pop.style.left = `${left}px`;
    } else {
      // absolute relative to nearest positioned ancestor (our header th wrapper)
      const containerRect = this.element.getBoundingClientRect();
      let top = rect.bottom - containerRect.top + margin;
      let left = rect.right - containerRect.left - popRect.width;
      // Clamp horizontally within the wrapper bounds
      left = Math.min(left, containerRect.width - popRect.width - margin);
      left = Math.max(left, margin);
      // If not enough space below within the container viewport, place above
      const availableBelow = window.innerHeight - rect.bottom;
      if (availableBelow < popRect.height + margin) {
        top = rect.top - containerRect.top - popRect.height - margin;
      }
      pop.style.top = `${top}px`;
      pop.style.left = `${left}px`;
    }
  }

  // Helpers for portaled content
  getInputEl() {
    const pop = this.popEl;
    return pop.querySelector('[data-filter-popover-target="input"]') || pop.querySelector('input');
  }

  attachPopoverEvents() {
    const pop = this.popEl;
    const applyBtn = pop.querySelector('[data-action~="filter-popover#apply"]');
    const clearBtn = pop.querySelector('[data-action~="filter-popover#clear"]');
    const closeBtn = pop.querySelector('[data-action~="filter-popover#close"]');
    const input = this.getInputEl();

    if (applyBtn) {
      const fn = (ev) => this.apply(ev);
      applyBtn.addEventListener('click', fn);
      this._cleanupFns.push(() => applyBtn.removeEventListener('click', fn));
    }
    if (clearBtn) {
      const fn = (ev) => this.clear(ev);
      clearBtn.addEventListener('click', fn);
      this._cleanupFns.push(() => clearBtn.removeEventListener('click', fn));
    }
    if (closeBtn) {
      const fn = (ev) => { ev.preventDefault(); this.close(); };
      closeBtn.addEventListener('click', fn);
      this._cleanupFns.push(() => closeBtn.removeEventListener('click', fn));
    }
    if (input) {
      const fn = (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); this.apply(ev); } };
      input.addEventListener('keydown', fn);
      this._cleanupFns.push(() => input.removeEventListener('keydown', fn));
    }
  }

  detachPopoverEvents() {
    while (this._cleanupFns.length) {
      const off = this._cleanupFns.pop();
      try { off(); } catch {}
    }
  }

  // Visual indicator (funnel) when a filter is active for this field
  updateActiveIcon() {
    const trigger = this.element.querySelector('[data-action*="filter-popover#open"]');
    if (!trigger) return;
    const params = new URLSearchParams(window.location.search);
    const filterName = this.fieldValue + '_filter';
    const active = !!(params.get(filterName) || '').trim();
    // Tailwind-based highlight for active filter icon
    trigger.classList.toggle('text-primary-600', active);
    trigger.classList.toggle('text-gray-400', !active);
  }
}
